import { toast } from '@navi/web-ui/lib/primitives/Toast/index';
import { createSlice } from '@reduxjs/toolkit';
import { getDeviceId } from 'src/utils/FingerPrintJS';
import { GLOBAL, setGlobalUserData } from '../constants/Global';
import { Roles } from '../pages/auth/constants/AuthConstants';
import {
  checkFieldTLAndClusterAndZonalManagers,
  isInternalTeamLead,
  notHideTabsInCaseOfSnooze
} from 'src/utils/RoleManagementHelper';
import { getServerTime } from 'src/utils/DateHelper';
import {
  CALL_BRIDGE_STATUSES,
  InteractionType
} from '../pages/CaseDetails/feedbackForm/interfaces/index.type';
import { LOCAL_STORAGE_KEYS } from '../constants/StorageKeys';

export enum ToastSeverity {
  SUCCESS = 'success',
  ERROR = 'error',
  INFO = 'info',
  WARNING = 'warning',
  STICKY = 'sticky'
}

export enum MandateStatus {
  SIGNED = 'SIGNED',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  DISABLED = 'DISABLED'
}

export enum UnifiedMandateStatus {
  INITIATED = 'INITIATED',
  FORM_CREATION_FAILED = 'FORM_CREATION_FAILED',
  FORM_CREATED = 'FORM_CREATED',
  SIGNED = 'SIGNED',
  SIGN_FAILURE = 'SIGN_FAILURE',
  FAILURE = 'FAILURE',
  MANDATE_REVOKED = 'MANDATE_REVOKED',
  REVOKED = 'REVOKED',
  MANDATE_EXPIRED = 'MANDATE_EXPIRED',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  DISABLED = 'DISABLED',
  IN_PROGRESS = 'IN_PROGRESS'
}

export enum RepaymentMode {
  E_NACH = 'E_NACH'
}
export enum ActivityStatus {
  ON_BREAK = 'ON_BREAK',
  ACTIVE = 'ACTIVE'
}

export enum CosmosSyncBlockStatus {
  SYNCED = 'SYNCED',
  WARNED = 'WARNED',
  BLOCKED = 'BLOCKED'
}
export enum AccessConsentStatus {
  CONSENT_GIVEN = 'CONSENT_GIVEN',
  CONSENT_PENDING = 'CONSENT_PENDING',
  BLOCKED = 'BLOCKED',
  UNDEFINED = 'UNDEFINED'
}

export enum SlashCallStatus {
  CONNECTING = 'Connecting...',
  RINGING = 'Ringing...',
  ANSWERED = 'Answered',
  ENDED = 'Ended'
}

export interface ToastData {
  message: string;
  severity: ToastSeverity;
  sticky?: boolean;
}

export interface User {
  isLoggedIn: boolean;
  token: string;
  roles?: Array<string>;
  name?: string;
  phoneNumber?: string;
  agencyCode?: string;
  agencyName?: string;
  referenceId?: string;
  deviceId: string;
  encryptedCrmId?: string;
  slashUsername?: string;
  slashTabOpened?: boolean;
  naviUser?: boolean;
  email?: string;
  ameyoKey?: string;
  ameyoUserName?: string;
  userImpersonated?: boolean;
  showCaptcha?: boolean;
  captchaVerified?: boolean;
  reloadCaptcha?: boolean;
  revampedDashboardViewAllowed?: boolean;
  revampedFeedbackViewAllowed?: boolean;
  campaignId?: string;
  longhornFirebaseToken?: string;
  agenciesReporting?: Array<string>;
  userActivityStatus: {
    activityStatus: ActivityStatus;
    breakStartTime: string;
  };
  withinOperativeHours: boolean;
  businessVerticals: Array<string>;
}

export interface DispositionData {
  accountNumber: string;
  customerId: string;
  caseReferenceId: string;
  businessVertical: string;
  name: string;
  referenceId: string;
}

export interface BlankDisposition {
  data: DispositionData[] | [] | undefined;
  disableBlankDisposition: boolean;
  // { data: [], disableBlankDisposition: true }
}

export interface AmeyoCallDetails {
  interactionId: string;
  recipientNumber: string;
  crtObjectId?: string;
}

export interface CallingWindow {
  callingStartTime: string; // Format: "HH:mm:ss"
  callingEndTime: string; // Format: "HH:mm:ss"
}

export interface RepaymentFailureDetails {
  repaymentMode?: RepaymentMode;
  reason?: string;
  orderCreationTimestamp?: string;
  nextEnachPresentationDate?: string;
}

export interface IGlobalSearch {
  phoneNumber: number;
  accountNumber: number;
  name: string;
  customerReferenceId: string;
}
export interface IGlobalPlayer {
  status: 'play' | 'pause' | 'stop';
  url: string;
  caseUrl: string;
  visible: boolean; // if false then unmount.
  startTime?: number;
  duration?: number;
  cursor?: number;
  current?: number;
  loading: boolean;
  history?: Array<IGlobalPlayer>;
  currentPlayerContainerId: string;
  secondsElapsedOfGlobalPlayer: number;
}

export enum HumanReminderType {
  PRE_DUE = 'PRE_DUE',
  POST_DUE = 'POST_DUE'
}

export interface UpcomingDueDetails {
  emiDueDate: string;
  upcomingDueAmount: {
    currency: string;
    amount: number;
  };
  upcomingPayableDueAmount: {
    currency: string;
    amount: number;
  };
}

export enum WAIVER_REQUEST_TYPE {
  HOLD = 'HOLD',
  WAIVER = 'WAIVER'
}

export interface IHumanReminderDues {
  emiDueDate: string;
  waiverRequestType: WAIVER_REQUEST_TYPE;
  nextIncrementDate: string;
  emiAmount: {
    currency: string;
    amount: number;
  };
  heldPenaltyCharge: {
    currency: string;
    amount: number;
  };
  penaltyCharges: {
    currency: string;
    amount: number;
  };
  nextIncrementAmount: {
    currency: string;
    amount: number;
  };
  emiAmountWithIncrementalPenaltyCharge: {
    currency: string;
    amount: number;
  };
  totalUnpaidDueAmount: {
    currency: string;
    amount: number;
  };
  totalEmiAmount: {
    currency: string;
    amount: number;
  };
  unpaidPenaltyCharges: {
    currency: string;
    amount: number;
  };
  paidAmount: {
    currency: string;
    amount: number;
  };
  penaltyApplied: {
    currency: string;
    amount: number;
  };
}

export interface IPtpInfoType {
  date: string;
  text: string;
  type: string;
  brokenPtpCount: number;
}

export interface HumanReminderCustomerDetails {
  humanReminderResponse: {
    customerDetails: {
      name: string;
      gender: string | null;
      age: number;
      state: string | null;
      primaryNumber: string;
      connectedNumber: string;
      customerReferenceId: string;
    };
    previousInteractionDetails: {
      callTimestamp: number;
      feedbackText: string | null;
      feedbackDate: string | null;
      interactionType: InteractionType;
      ptpDetailsIfAny: IPtpInfoType;
    };
    loanDetails: {
      disbursedAmount: {
        currency: string;
        amount: number;
      };
      latestWtpDate?: string;
      loanAccountNumber: string;
      totalAmountDue: {
        currency: string;
        amount: number;
      };
      currentDpd: number;
      unpaidDues: IHumanReminderDues[];
      holdEligibilityDues: IHumanReminderDues[];
      nextDayDues: IHumanReminderDues[];
      mandateStatus: MandateStatus;
      unifiedMandateStatus: UnifiedMandateStatus;
      repaymentFailureDetails: RepaymentFailureDetails;
      upcomingDueDetails: UpcomingDueDetails;
      totalTenureMonths: number;
      remainingAmount: {
        currency: string;
        amount: number;
      };
      remainingTenureMonths: number;
      lastPaidDpd: number;
      mandateInProgress?: boolean;
      lastPaidTimestamp: string;
      duesPaidLate: number;
      duesPaidOnTime: number;
    };
    paymentLinkOptions: {
      label: string;
      value: string;
      amount: {
        currency: string;
        amount: number | null;
      };
    }[];
    source: string;
    acceptCallTime: number;
    status: string;
    reminderRefId: string;
    interactionRefId: string;
    reminderType: HumanReminderType;
  };
}

export interface HumanReminderAgentDetails {
  totalCalls: number;
  onlineTime: number;
  requestProcessedAt: number;
}

export interface GenerateAmeyoPassword {
  ameyoKey: string;
  campaignId?: string;
  campaignMappingStatus?: string;
  loading: boolean;
  email: string;
}

export interface IFeatureFlags {
  [key: string]: boolean;
}

export interface IActionEvent {
  actionReferenceId: string;
  actionType: string;
  actionLabel: string;
  unAcknowledged: boolean;
}
export interface ICosmosVersionData {
  downloadPreSignedUrl: string;
  appVersion: string;
  appType: string;
  releaseType: string;
  buildNumber: number;
}

export interface CommonState {
  userData: User;
  toast?: ToastData;
  hideSideNav: boolean;
  showStickyNote: boolean;
  globalPlayer?: IGlobalPlayer;
  globalSearch?: IGlobalSearch[];
  fieldVisitModalId: string;
  blankDisposition: BlankDisposition;
  fcmToken?: string;
  ameyoCallDetails: AmeyoCallDetails;
  humanReminderAgentDetails: HumanReminderAgentDetails;
  humanReminderCustomerDetails: HumanReminderCustomerDetails;
  humanReminderCustomerImage: string;
  isPerformanceDashboardVisible: boolean;
  isTeamLead: boolean;
  isCiuAgent: boolean;
  isPincodeMappingVisible: boolean;
  humanReminderCustomerDetailsLoading: boolean;
  generateAmeyoPassword: GenerateAmeyoPassword;
  isInternalTeamLead?: boolean;
  isCallingAgent?: boolean;
  feedbackFilters: string;
  serverDate: Date;
  featureFlags: IFeatureFlags;
  isLonghornThemeEnabled: boolean;
  isAmeyoUtilityVisible?: boolean;
  isAmeyoGeneratePasswordVisible?: boolean;
  extensionVersion: string;
  isAgentOnline: boolean;
  slashCallStatus: CALL_BRIDGE_STATUSES;
  breakTimeStarted: string;
  cosmosSyncBlockStatus: CosmosSyncBlockStatus;
  lastSyncTimestamp: number;
  isCosmosSnapshotDisabled: boolean;
  slashCallStatusFromExtension: SlashCallStatus;
  agentBusinessVertical: string;
  callingWindow?: CallingWindow;
  lastSyncPermissionStatus?: boolean;
  shouldNotHidePauseCasesTab: boolean;
  agentEscalationCount: number;
  showLogoutModal?: boolean;
  actionEvents: IActionEvent[];
  cosmosVersionData: ICosmosVersionData;
  isCosmosUpdateRequired: boolean;
  accessConsentStatus?: AccessConsentStatus;
  userAppVersion: string;
  isWhatsappCallRecordingEnabled: boolean;
}

export const CHECK_LOGIN = 'CHECK_LOGIN';
export enum CHECK_LOGIN_STATUS {
  TRUE = 'TRUE',
  FALSE = 'FALSE'
}

const slashCallStatusFromLocalStorage = localStorage.getItem('slashCallStatus');
const initialState = {
  userData: {
    isLoggedIn: true,
    token: localStorage.getItem('LONGHORN_SESSION_TOKEN') || '',
    deviceId: getDeviceId(),
    agentId: '',
    slashTabOpened: false,
    longhornFirebaseToken: localStorage.getItem(LOCAL_STORAGE_KEYS.LONGHORN_FIREBASE_TOKEN) || ''
  } as User,
  hideSideNav: false,
  showStickyNote: false,
  blankDisposition: { data: undefined, disableBlankDisposition: true },
  fcmToken: '',
  globalPlayer: {
    status: 'stop',
    url: '',
    visible: false,
    startTime: 0
  },
  globalSearch: [] as IGlobalSearch[],
  humanReminderAgentDetails: {
    totalCalls: 0,
    onlineTime: 0,
    requestProcessedAt: 0
  },
  humanReminderCustomerDetails: {},
  fieldVisitModalId: '',
  ameyoCallDetails: {},
  isPerformanceDashboardVisible: false,
  isPincodeMappingVisible: false,
  isAmeyoUtilityVisible: false,
  isAmeyoGeneratePasswordVisible: false,
  featureFlags: {},
  humanReminderCustomerDetailsLoading: false,
  generateAmeyoPassword: {
    ameyoKey: '',
    campaignId: '',
    campaignMappingStatus: '',
    loading: false,
    email: ''
  },
  humanReminderCustomerImage: '',
  isInternalTeamLead: false,
  isCallingAgent: false,
  serverDate: new Date(getServerTime()).toString(),
  feedbackFilters: '',
  isLonghornThemeEnabled: false,
  extensionVersion: '',
  isAgentOnline: false,
  slashCallStatus: CALL_BRIDGE_STATUSES.DEFAULT,
  breakTimeStarted: '',
  cosmosSyncBlockStatus: CosmosSyncBlockStatus.SYNCED,
  lastSyncTimestamp: 0,
  isCosmosSnapshotDisabled: true,
  slashCallStatusFromExtension: slashCallStatusFromLocalStorage,
  agentBusinessVertical: '',
  callingWindow: {
    callingStartTime: '08:00:00',
    callingEndTime: '19:00:00'
  },
  shouldNotHidePauseCasesTab: false,
  isTeamLead: false,
  isCiuAgent: false,
  lastSyncPermissionStatus: false,
  agentEscalationCount: 0,
  showLogoutModal: false,
  actionEvents: [],
  cosmosVersionData: {
    downloadPreSignedUrl: '',
    appVersion: '',
    appType: '',
    releaseType: 0,
    buildNumber: 0
  },
  isCosmosUpdateRequired: false,
  accessConsentStatus: AccessConsentStatus.UNDEFINED,
  userAppVersion: '',
  isWhatsappCallRecordingEnabled: false
} as CommonState;

setGlobalUserData({ token: initialState.userData.token });
export const commonSlice = createSlice({
  name: 'common',
  initialState,
  reducers: {
    setAuthData: (state, action) => {
      if (action.payload) {
        setGlobalUserData({ token: action.payload.token, userAgent: action.payload.referenceId });
      }
      const roles = action.payload?.roles;
      const isNaviUser = action.payload?.naviUser;
      if (roles?.length) {
        state.isPerformanceDashboardVisible =
          action.payload?.featureFlags?.performanceDashboard ?? false;

        const isPincodeMappingVisible = action.payload?.featureFlags?.agencyServiceability ?? false;
        const isAmeyoUtilityVisible = action.payload?.featureFlags?.ameyoSelfServeFlag ?? false;
        const isAmeyoGeneratePasswordVisible =
          action.payload?.featureFlags?.ameyoGeneratePasswordFlag ?? false;

        state.featureFlags = action.payload?.featureFlags;

        state.isAmeyoUtilityVisible = isAmeyoUtilityVisible;
        state.isAmeyoGeneratePasswordVisible = isAmeyoGeneratePasswordVisible;
        state.isPincodeMappingVisible = isPincodeMappingVisible;

        state.isCallingAgent = roles?.includes(Roles.ROLE_CALLING_AGENT);
        state.isTeamLead = [
          Roles.ROLE_TEAM_LEAD,
          Roles.ROLE_COLLECTION_HEAD,
          Roles.ROLE_SENIOR_TEAM_LEAD
        ].some(role => roles?.includes(role));
        state.isCiuAgent = roles?.includes(Roles.ROLE_CENTRAL_UNIT);
        state.isInternalTeamLead = isInternalTeamLead(roles, isNaviUser);
        state.fieldTLAndClusterAndZonalManagers = checkFieldTLAndClusterAndZonalManagers(
          roles,
          isNaviUser
        );
        state.shouldNotHidePauseCasesTab = notHideTabsInCaseOfSnooze(roles, isNaviUser);
      }
      state.extensionVersion = action.payload.ameyoExtensionVersion;
      state.userData = { ...state.userData, ...action.payload };
    },
    setToast(state, action) {
      toast(action.payload.message, { type: action.payload.severity });
      state.toast = action.payload;
    },
    navbarStatus(state, action) {
      state.hideSideNav = action.payload;
    },
    setShowStickyNote(state, action) {
      state.showStickyNote = action.payload;
    },
    setFcmToken(state, action) {
      state.fcmToken = action.payload;
    },
    setDeviceIdInStore(state, action) {
      state.userData.deviceId = action.payload;
    },

    // [START] audio player actions
    setCursor(state, action) {
      if (!state.globalPlayer) return;

      const { cursor, currentPlayerContainerId, url, status } = action.payload;

      state.globalPlayer.cursor = cursor;
      state.globalPlayer.caseUrl = window.location.href;
      state.globalPlayer.visible = true;
      state.globalPlayer.url = url || state.globalPlayer.url;
      state.globalPlayer.status = status || 'play';
      state.globalPlayer.currentPlayerContainerId =
        currentPlayerContainerId || state.globalPlayer.currentPlayerContainerId;
    },
    setGlobalSearch(state, action) {
      state.globalSearch = action.payload;
    },
    setCursorPointer(state, action) {
      if (!state.globalPlayer) return;
      const { cursor } = action.payload;
      state.globalPlayer.visible = true;
      state.globalPlayer.cursor = cursor;
    },
    setSecondsElapsedOfGlobalPlayer(state, action) {
      if (!state.globalPlayer) return;
      state.globalPlayer.secondsElapsedOfGlobalPlayer = action.payload;
    },
    playPause(state, action) {
      if (!state.globalPlayer) return;
      state.globalPlayer.visible = true;
      state.globalPlayer.status = action.payload;
    },
    closePlayer(state) {
      if (!state.globalPlayer) return;
      state.globalPlayer.cursor = 0;
      state.globalPlayer.status = 'stop';
      state.globalPlayer.visible = false;
    },
    setCaptchaVerified(state, action) {
      state.userData.captchaVerified = action.payload;
    },
    setCaptchaVisibility(state, action) {
      state.userData.showCaptcha = action.payload;
    },
    setReloadCaptcha(state, action) {
      state.userData.reloadCaptcha = action.payload;
    },
    setFieldVisitModalId(state, action) {
      state.fieldVisitModalId = action.payload;
    },
    setBlankDisposition(state, action) {
      const { blankDispositions, status = '' } = action.payload;
      const disableBlankDisposition = status === 'FAILED' ? false : true;
      state.blankDisposition = { data: blankDispositions, disableBlankDisposition };
    },
    setAmeyoCallDetails(state, action) {
      state.ameyoCallDetails = action.payload;
    },
    setHumanReminderAgentDetails(state, action) {
      state.humanReminderAgentDetails = action.payload;
    },
    setHumanReminderCustomerDetails(state, action) {
      state.humanReminderCustomerDetails = action.payload;
      state.humanReminderCustomerDetailsLoading = false;
    },
    resetHumanReminderCustomerDetails(state, action) {
      state.humanReminderCustomerDetails = {} as HumanReminderCustomerDetails;
      state.humanReminderCustomerImage = '';
    },
    setHumanReminderCustomerDetailsLoading(state, action) {
      state.humanReminderCustomerDetailsLoading = action.payload;
    },
    setGeneratedAmeyoPassword(state, action) {
      state.generateAmeyoPassword.ameyoKey = action.payload;
      state.generateAmeyoPassword.loading = false;
    },
    setGeneratedCampaignId(state, action) {
      state.generateAmeyoPassword.campaignId = action.payload;
      state.generateAmeyoPassword.loading = false;
    },
    setGeneratedCampaignMappingStatus(state, action) {
      state.generateAmeyoPassword.campaignMappingStatus = action.payload;
      state.generateAmeyoPassword.loading = false;
    },
    setAmeyoGeneratePasswordLoading(state, action) {
      state.generateAmeyoPassword.loading = action.payload;
    },
    setAmeyoGeneratePasswordEmail(state, action) {
      state.generateAmeyoPassword.email = action.payload;
    },
    setHumanReminderCustomerImage(state, action) {
      state.humanReminderCustomerImage = action.payload;
    },
    setServerDate(state, action) {
      state.serverDate = action.payload;
    },
    setFeedbackFilters(state, action) {
      state.feedbackFilters = action.payload;
    },
    setIsLonghornThemeEnabled(state, action) {
      state.isLonghornThemeEnabled = action.payload;
    },
    setExtensionVersion(state, action) {
      state.extensionVersion = action.payload;
    },
    setLonghornFirebaseToken(state, action) {
      state.userData.longhornFirebaseToken = action.payload;
      localStorage.setItem(LOCAL_STORAGE_KEYS.LONGHORN_FIREBASE_TOKEN, action.payload);
    },
    setIsAgentOnline(state, action) {
      state.isAgentOnline = action.payload;
    },
    setSlashCallStatus(state, action) {
      state.slashCallStatus = action.payload;
    },
    setBreakTimeStarted(state, action) {
      state.breakTimeStarted = action.payload;
    },
    setCosmosSyncBlockStatus(state, action) {
      GLOBAL.LONGHORN_BLOCK_STATUS = action.payload;
      state.cosmosSyncBlockStatus = action.payload;
    },
    setLastSyncTimestamp(state, action) {
      state.lastSyncTimestamp = action.payload;
    },
    setLastSyncPermissionStatus(state, action) {
      state.lastSyncPermissionStatus = action.payload;
    },
    setIsCosmosSnapshotDisabled(state, action) {
      state.isCosmosSnapshotDisabled = action.payload;
    },
    setSlashCallStatusFromExtension(state, action) {
      state.slashCallStatusFromExtension = action.payload;
    },
    setAgentBusinessVertical(state, action) {
      state.agentBusinessVertical = action.payload;
    },
    setCallingWindow(state, action) {
      state.callingWindow = action.payload;
    },
    setAgentEscalationCount(state, action) {
      state.agentEscalationCount = action.payload;
    },
    setShowLogoutModal(state, action) {
      state.showLogoutModal = action.payload;
    },
    setActionEvents(state, action) {
      state.actionEvents = action.payload;
    },
    setCosmosVersionData(state, action) {
      state.cosmosVersionData = action.payload;
    },
    setIsCosmosUpdateRequired(state, action) {
      state.isCosmosUpdateRequired = action.payload;
    },
    setAccessConsentStatus(state, action) {
      state.accessConsentStatus = action.payload;
    },
    setUserAppVersion(state, action) {
      state.userAppVersion = action.payload;
    },
    setWhatsappCallRecordingEnabled(state, action: { payload: boolean }) {
      state.isWhatsappCallRecordingEnabled = action.payload;
    }
  }
});

export const {
  setAuthData,
  setToast,
  navbarStatus,
  setShowStickyNote,
  setDeviceIdInStore,
  setCaptchaVisibility,
  setCaptchaVerified,
  setReloadCaptcha,
  setFieldVisitModalId,
  setFcmToken,
  setCursor,
  setGlobalSearch,
  setCursorPointer,
  playPause,
  closePlayer,
  setSecondsElapsedOfGlobalPlayer,
  setHumanReminderAgentDetails,
  setHumanReminderCustomerDetails,
  resetHumanReminderCustomerDetails,
  setAmeyoCallDetails,
  setBlankDisposition,
  setHumanReminderCustomerDetailsLoading,
  setHumanReminderCustomerImage,
  setFeedbackFilters,
  setGeneratedAmeyoPassword,
  setGeneratedCampaignId,
  setGeneratedCampaignMappingStatus,
  setAmeyoGeneratePasswordLoading,
  setAmeyoGeneratePasswordEmail,
  setServerDate,
  setIsLonghornThemeEnabled,
  setExtensionVersion,
  setLonghornFirebaseToken,
  setIsAgentOnline,
  setSlashCallStatus,
  setBreakTimeStarted,
  setCosmosSyncBlockStatus,
  setLastSyncTimestamp,
  setIsCosmosSnapshotDisabled,
  setSlashCallStatusFromExtension,
  setAgentBusinessVertical,
  setCallingWindow,
  setLastSyncPermissionStatus,
  setAgentEscalationCount,
  setShowLogoutModal,
  setActionEvents,
  setCosmosVersionData,
  setIsCosmosUpdateRequired,
  setAccessConsentStatus,
  setUserAppVersion,
  setWhatsappCallRecordingEnabled
} = commonSlice.actions;

export default commonSlice.reducer;
